/**
 * VoiceRecipeLogo Component
 *
 * Pure HTML/CSS logo — no images, no external resources.
 * Theme-aware: uses white text on dark backgrounds, black text on light.
 * Renders the waveform icon + "VoiceRecipe" wordmark.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4
 */

import { Box, useTheme } from '@mui/material';

export interface VoiceRecipeLogoProps {
    /** Controls overall scale. small=20px, medium=28px, large=36px waveform height */
    size?: 'small' | 'medium' | 'large';
}

const sizeConfig = {
    small: { height: 18, barWidth: 3, fontSize: 16, gap: 6, radius: 1.5 },
    medium: { height: 24, barWidth: 4, fontSize: 20, gap: 8, radius: 2 },
    large: { height: 30, barWidth: 5, fontSize: 24, gap: 10, radius: 2.5 },
};

// Bar heights as proportions of total height (from SVG: 14/14, 11/14, 6/14, 11/14, 14/14)
const barProportions = [1, 11 / 14, 6 / 14, 11 / 14, 1];
// Top offsets as proportions (from SVG: 0/14, 3/14, 5/14, 0/14, 0/14)
const topProportions = [0, 3 / 14, 5 / 14, 0, 0];

export function VoiceRecipeLogo({ size = 'medium' }: VoiceRecipeLogoProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const voiceColor = isDark ? '#ffffff' : '#000000';
    const recipeColor = isDark ? '#a78bfa' : '#7c3aed';
    const barColor = isDark ? '#a78bfa' : '#7c3aed';

    const config = sizeConfig[size];

    return (
        <Box
            sx={{ display: 'inline-flex', alignItems: 'center', gap: `${config.gap}px` }}
            aria-label="VoiceRecipe"
            role="img"
        >
            {/* Waveform bars */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '3px', height: config.height }}>
                {barProportions.map((proportion, i) => (
                    <Box
                        key={i}
                        sx={{
                            width: config.barWidth,
                            height: Math.round(config.height * proportion),
                            marginTop: `${Math.round(config.height * topProportions[i])}px`,
                            backgroundColor: barColor,
                            borderRadius: `${config.radius}px`,
                        }}
                    />
                ))}
            </Box>

            {/* Wordmark */}
            <Box
                component="span"
                sx={{
                    fontSize: config.fontSize,
                    fontWeight: 700,
                    letterSpacing: '-0.01em',
                    lineHeight: 1,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
            >
                <Box component="span" sx={{ color: voiceColor }}>Voice</Box>
                <Box component="span" sx={{ color: recipeColor }}>Recipe</Box>
            </Box>
        </Box>
    );
}
